import React, { useEffect, useState, useMemo } from 'react';
import { ClipboardCheck, CheckCircle2, XCircle, AlertTriangle, Loader2, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { supplierUpdatesAPI } from '../lib/api';
import { toast } from 'sonner';
import { formatPrice } from '../lib/utils';

/**
 * Page admin: validation des mises a jour fournisseur en attente.
 * Affiche le diff (valeur actuelle vs valeur proposee) pour chaque produit
 * et permet d'approuver/rejeter en masse.
 */

const FIELD_LABELS = {
    price: 'Prix',
    stock: 'Stock',
    description: 'Description',
    calibre: 'Calibre',
    origin: 'Origine',
    min_order: 'Cmd min.',
    contenance: 'Contenance',
    season_start: 'Saison debut',
    season_end: 'Saison fin',
};

const formatVal = (field, val) => {
    if (val === null || val === undefined || val === '') return '—';
    if (field === 'price') return formatPrice(val);
    return String(val);
};

export default function AdminSupplierUpdates() {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [confirmAction, setConfirmAction] = useState(null); // 'approve' | 'reject' | null
    const [acting, setActing] = useState(false);

    const loadData = async () => {
        try {
            const res = await supplierUpdatesAPI.getPending();
            setProducts(res.data.products || []);
        } catch (e) {
            console.error(e);
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadData(); }, []);

    const allSelected = products.length > 0 && selectedIds.size === products.length;
    const toggleAll = () => setSelectedIds(allSelected ? new Set() : new Set(products.map(p => p.id)));
    const toggleOne = (id) => {
        setSelectedIds(prev => {
            const n = new Set(prev);
            if (n.has(id)) n.delete(id); else n.add(id);
            return n;
        });
    };

    const handleApply = async () => {
        if (!confirmAction || selectedIds.size === 0) return;
        setActing(true);
        try {
            const ids = Array.from(selectedIds);
            const res = confirmAction === 'approve'
                ? await supplierUpdatesAPI.approve(ids)
                : await supplierUpdatesAPI.reject(ids);
            toast.success(res.data.message);
            setSelectedIds(new Set());
            setConfirmAction(null);
            await loadData();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur lors de l'operation");
        } finally {
            setActing(false);
        }
    };

    const computeChanges = (p) => {
        const pending = p.pending_changes || {};
        const changed = [];
        for (const [field, newVal] of Object.entries(pending)) {
            const oldVal = p[field];
            if (oldVal !== newVal) {
                changed.push({ field, oldVal, newVal });
            }
        }
        return changed;
    };

    const stats = useMemo(() => ({
        total: products.length,
        suppliers: new Set(products.map(p => (p.product_code || '').split('-')[0])).size,
    }), [products]);

    if (loading) {
        return (
            <div className="min-h-screen bg-sand flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-jungle" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-sand bg-botanical" data-testid="admin-supplier-updates-page">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                            <ClipboardCheck className="w-6 h-6 text-amber-700" />
                        </div>
                        <div>
                            <h1 className="font-heading text-3xl font-bold tracking-tight text-jungle">Mises a jour fournisseur</h1>
                            <p className="text-muted-foreground text-sm">
                                {stats.total} produit{stats.total !== 1 ? 's' : ''} en attente de validation
                                {stats.suppliers > 0 && ` (${stats.suppliers} fournisseur${stats.suppliers !== 1 ? 's' : ''})`}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Bulk action bar */}
                {selectedIds.size > 0 && (
                    <Card className="border-2 border-amber-300 bg-amber-50/50 mb-4 rounded-2xl" data-testid="bulk-action-bar">
                        <CardContent className="py-3 flex flex-wrap items-center justify-between gap-3">
                            <span className="font-medium text-amber-900">{selectedIds.size} produit(s) selectionne(s)</span>
                            <div className="flex gap-2">
                                <Button
                                    onClick={() => setConfirmAction('reject')}
                                    variant="outline"
                                    className="border-red-300 text-red-700 hover:bg-red-50 rounded-xl"
                                    data-testid="reject-selected-btn"
                                >
                                    <XCircle className="w-4 h-4 mr-2" /> Rejeter
                                </Button>
                                <Button
                                    onClick={() => setConfirmAction('approve')}
                                    className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
                                    data-testid="approve-selected-btn"
                                >
                                    <CheckCircle2 className="w-4 h-4 mr-2" /> Approuver
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Empty state */}
                {products.length === 0 && (
                    <Card className="border-0 bg-white/80 rounded-2xl">
                        <CardContent className="py-16 text-center">
                            <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
                            <h3 className="font-heading text-xl text-jungle mb-2">Tout est a jour !</h3>
                            <p className="text-muted-foreground">Aucune modification fournisseur en attente.</p>
                        </CardContent>
                    </Card>
                )}

                {/* Table */}
                {products.length > 0 && (
                    <Card className="border-0 bg-white/90 backdrop-blur-sm shadow-sm rounded-2xl">
                        <CardHeader>
                            <CardTitle className="font-heading text-jungle text-base flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4 text-amber-600" />
                                Modifications proposees
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="w-10">
                                            <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="select-all-cb" />
                                        </TableHead>
                                        <TableHead>Produit</TableHead>
                                        <TableHead>Code</TableHead>
                                        <TableHead>Modifications proposees</TableHead>
                                        <TableHead>Soumis le</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {products.map(p => {
                                        const changes = computeChanges(p);
                                        const submittedAt = p.supplier_updated_at
                                            ? new Date(p.supplier_updated_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })
                                            : '—';
                                        return (
                                            <TableRow key={p.id} data-testid={`pending-row-${p.id}`}>
                                                <TableCell>
                                                    <Checkbox
                                                        checked={selectedIds.has(p.id)}
                                                        onCheckedChange={() => toggleOne(p.id)}
                                                        data-testid={`select-cb-${p.id}`}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <div className="font-medium text-jungle">{p.name}</div>
                                                    {p.calibre && <div className="text-xs text-muted-foreground">{p.calibre}</div>}
                                                </TableCell>
                                                <TableCell>
                                                    <Badge variant="outline" className="font-mono text-xs">{p.product_code || '—'}</Badge>
                                                </TableCell>
                                                <TableCell>
                                                    {changes.length === 0 ? (
                                                        <span className="text-xs text-muted-foreground italic">aucun changement detecte</span>
                                                    ) : (
                                                        <div className="space-y-1">
                                                            {changes.map(c => (
                                                                <div key={c.field} className="text-sm flex items-center gap-2 flex-wrap">
                                                                    <span className="font-medium text-jungle/70 min-w-[100px]">{FIELD_LABELS[c.field] || c.field}:</span>
                                                                    <span className="text-muted-foreground line-through">{formatVal(c.field, c.oldVal)}</span>
                                                                    <ArrowRight className="w-3 h-3 text-jungle/40" />
                                                                    <span className="font-semibold text-emerald-700">{formatVal(c.field, c.newVal)}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </TableCell>
                                                <TableCell className="text-xs text-muted-foreground">{submittedAt}</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                )}
            </div>

            {/* Confirmation dialog */}
            <AlertDialog open={!!confirmAction} onOpenChange={(o) => !o && setConfirmAction(null)}>
                <AlertDialogContent className="bg-sand">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-jungle">
                            {confirmAction === 'approve' ? 'Approuver les modifications ?' : 'Rejeter les modifications ?'}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {confirmAction === 'approve'
                                ? `${selectedIds.size} produit(s) seront mis a jour avec les nouvelles valeurs proposees par le fournisseur.`
                                : `${selectedIds.size} modification(s) seront rejetees. Les produits garderont leurs valeurs actuelles.`}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-xl" disabled={acting}>Annuler</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleApply}
                            disabled={acting}
                            className={`rounded-xl ${confirmAction === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-destructive hover:bg-destructive/90'}`}
                            data-testid="confirm-action-btn"
                        >
                            {acting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                            {confirmAction === 'approve' ? 'Approuver' : 'Rejeter'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
